import {
  isFlightSavedItemData,
  isChatFlightSavedItemData,
  isLeFlightSavedItemData,
  type TransportConnection,
  type TransportOption,
  type TripItineraryData,
} from 'tripPlanner/api/types'
import { FlightsFareTypes } from 'constants/flight'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { parseSearchParams } from '../ShortlistedFlightsSection/flightShortlistUtils'

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}/

export interface FlightSearchDrawerParams {
  /** Planned-flight airports come from the saved connection, including unknown endpoints. */
  useSavedAirports?: boolean
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  /**
   * Every leg of a multi-city search, so reopening the drawer restores the whole
   * itinerary. Only set for multi-city; a single leg is described by the
   * origin/destination/date fields above.
   */
  itinerary?: FlightSearchParams['itinerary']
}

function getFlightDateParam(value: string | undefined): string | undefined {
  return value?.match(DATE_PARAM_PATTERN)?.[0]
}

export function findTransportConnectionForOption(
  itineraryData: TripItineraryData | undefined,
  itemId: TransportOption['itemId'] | undefined,
): TransportConnection | undefined {
  if (itemId === undefined) return undefined
  const targetItemId = String(itemId)

  for (const stop of itineraryData?.stops ?? []) {
    for (const transport of stop.transports ?? []) {
      if (transport.options.some(option => String(option.itemId) === targetItemId)) {
        return transport
      }
    }
  }

  return undefined
}

/**
 * A multi-city leg persists the FULL itinerary in its search params, so this
 * recovers every leg. Anything shorter than two legs isn't a multi-city search
 * we can reopen, and handing it back would have the drawer run a one-leg
 * multi-city search, which the search API rejects.
 */
function getMultiCityItinerary(
  searchParams: ReturnType<typeof parseSearchParams>,
): FlightSearchParams['itinerary'] | undefined {
  if (searchParams?.fareType !== FlightsFareTypes.MULTI_CITY) return undefined
  return searchParams.itinerary.length > 1 ? searchParams.itinerary : undefined
}

export function getFlightSearchDrawerParamsFromTransport(
  option: TransportOption,
  transport: TransportConnection | undefined,
  returnOption?: TransportOption,
): FlightSearchDrawerParams {
  const savedItemData = isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnLegSavedItemDataInput = returnOption?.savedItemData ?? option.returnLeg?.savedItemData
  const returnLegSavedItemData = isFlightSavedItemData(returnLegSavedItemDataInput) ? returnLegSavedItemDataInput : undefined
  const parsedSearchParams = isLeFlightSavedItemData(savedItemData)
    ? parseSearchParams(savedItemData.searchParams)
    : undefined
  const firstLeg = parsedSearchParams?.itinerary[0]
  // itinerary[1] is the return leg for a return fare, but the second city for a
  // multi-city one — reading a return date off it would be wrong.
  const returnLeg = parsedSearchParams?.fareType === FlightsFareTypes.MULTI_CITY
    ? undefined
    : parsedSearchParams?.itinerary[1]
  const flightSearchDefaults = transport?.flightSearchDefaults
  const useSavedAirports = isChatFlightSavedItemData(savedItemData)
  const airportDefaults = useSavedAirports ? undefined : flightSearchDefaults

  return {
    ...(useSavedAirports ? { useSavedAirports: true } : {}),
    originAirportCode: firstLeg?.departureAirport ?? savedItemData?.departureAirport ?? airportDefaults?.originAirportCode,
    originAirportName: firstLeg?.departureAirport || savedItemData?.departureAirport ? undefined : airportDefaults?.originAirportName,
    destinationAirportCode: firstLeg?.arrivalAirport ?? savedItemData?.arrivalAirport ?? airportDefaults?.destinationAirportCode,
    destinationAirportName: firstLeg?.arrivalAirport || savedItemData?.arrivalAirport ? undefined : airportDefaults?.destinationAirportName,
    departureDate: firstLeg?.departureDate ?? savedItemData?.departureDate ?? getFlightDateParam(savedItemData?.departureTime) ?? flightSearchDefaults?.departureDate,
    returnDate: returnLeg?.departureDate ?? returnLegSavedItemData?.departureDate ?? getFlightDateParam(returnLegSavedItemData?.departureTime),
    fareType: returnOption || option.returnLeg ? 'return' : parsedSearchParams?.fareType,
    fareClass: parsedSearchParams?.fareClass,
    occupants: parsedSearchParams?.occupancies[0],
    itinerary: getMultiCityItinerary(parsedSearchParams),
  }
}

export function applyRecentFlightSearchDefaults(
  params: FlightSearchDrawerParams,
  itineraryData: TripItineraryData | undefined,
  stopId: number | null | undefined,
  options?: { preferParamsAsSelected?: boolean },
): FlightSearchDrawerParams {
  const stop = itineraryData?.stops.find((candidate) => candidate.id === stopId)
  const resolved = resolveSearchDefaults({
    surface: 'flights',
    stopId,
    recentSearches: itineraryData?.recentSearches,
    ...(options?.preferParamsAsSelected ? { selected: params } : {}),
    savedFlightParams: params,
    indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
    stopIndicativeTravellerParty: stop?.indicativeTravellerParty,
    tripIndicativeTravellerParty: itineraryData?.indicativeTravellerParty,
    pastTripTravellerParty: itineraryData?.pastTripTravellerParty,
    productDefaults: { occupants: { adults: 2 } },
  })

  if (resolved.surface !== 'flights') {
    return params
  }

  const airports = params.useSavedAirports ? params : {
    originAirportCode: resolved.originAirportCode ?? params.originAirportCode,
    originAirportName: resolved.originAirportName ?? params.originAirportName,
    destinationAirportCode: resolved.destinationAirportCode ?? params.destinationAirportCode,
    destinationAirportName: resolved.destinationAirportName ?? params.destinationAirportName,
  }

  return {
    ...params,
    originAirportCode: airports.originAirportCode,
    originAirportName: airports.originAirportName,
    destinationAirportCode: airports.destinationAirportCode,
    destinationAirportName: airports.destinationAirportName,
    departureDate: resolved.departureDate ?? params.departureDate,
    returnDate: resolved.returnDate ?? params.returnDate,
    fareType: resolved.fareType ?? params.fareType,
    fareClass: resolved.fareClass ?? params.fareClass,
    occupants: resolved.occupants ?? params.occupants,
    itinerary: params.itinerary,
  }
}
